import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const flashGreen = keyframes`
  0%   { background-color: transparent; }
  25%  { background-color: rgba(76, 175, 80, 0.22); }
  100% { background-color: transparent; }
`;

const DEVICE_TYPE_LABEL: Record<string, string> = {
  'Intelbras Camera': 'Câmera',
  'Intelbras Controller': 'Controlador',
  Hikvision: 'Controlador',
  Topdata: 'Controlador',
  ControlId: 'Controlador',
  LPR: 'Câmera LPR',
};

const getTypeLabel = (type: string): string => DEVICE_TYPE_LABEL[type] ?? type;

// Hardcoded em vez do theme: error.main/info.main têm contraste ruim no fundo escuro deste componente.
const ERROR_COLOR = '#ff5252';
const ALREADY_SYNCED_COLOR = '#4dabf7';

export type DeviceLogStatus =
  | 'pending'
  | 'processing'
  | 'ok'
  | 'already_synced'
  | 'error';

export interface DeviceLogEntry {
  device: string;
  type: string;
  status: DeviceLogStatus;
  error?: string;
}

interface FaceDeviceLogProps {
  devices: DeviceLogEntry[];
}

interface DeviceRowProps {
  entry: DeviceLogEntry;
  isLast: boolean;
  flash?: boolean;
}

const DeviceRow = ({ entry, isLast, flash }: DeviceRowProps) => {
  const isProcessing = entry.status === 'processing';
  const isError = entry.status === 'error';
  const isOk = entry.status === 'ok';
  const isAlreadySynced = entry.status === 'already_synced';

  const typeLabel = getTypeLabel(entry.type);

  const icon = {
    pending: (
      <HourglassEmptyIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
    ),
    processing: (
      <SyncIcon
        sx={{
          fontSize: 18,
          color: 'info.main',
          animation: `${spin} 1.2s linear infinite`,
        }}
      />
    ),
    ok: <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />,
    already_synced: (
      <DoneAllIcon sx={{ fontSize: 18, color: ALREADY_SYNCED_COLOR }} />
    ),
    error: <ErrorIcon sx={{ fontSize: 18, color: ERROR_COLOR }} />,
  }[entry.status];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        pl: 1,
        pr: 1.25,
        py: 1.1,
        borderBottom: isLast ? 'none' : (t) => `1px solid ${t.palette.divider}`,
        borderLeft: '3px solid',
        borderLeftColor: isProcessing
          ? 'info.main'
          : isError
            ? ERROR_COLOR
            : isOk
              ? 'success.main'
              : isAlreadySynced
                ? ALREADY_SYNCED_COLOR
                : 'transparent',
        backgroundColor: isProcessing
          ? (t) => alpha(t.palette.info.main, 0.1)
          : isError
            ? alpha(ERROR_COLOR, 0.1)
            : isOk
              ? (t) => alpha(t.palette.success.main, 0.06)
              : isAlreadySynced
                ? alpha(ALREADY_SYNCED_COLOR, 0.08)
                : (t) => alpha(t.palette.common.white, 0.03),
        opacity: isOk || isAlreadySynced ? 0.85 : 1,
        animation: flash ? `${flashGreen} 0.7s ease-out` : 'none',
        transition: 'background-color 0.3s ease',
        '&:hover': { filter: 'brightness(1.12)' },
      }}
    >
      <Box sx={{ flexShrink: 0, display: 'flex', pt: entry.error ? 0.2 : 0 }}>
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 13.5,
            fontWeight: isProcessing ? 600 : 400,
            color: isProcessing
              ? 'info.main'
              : isError
                ? ERROR_COLOR
                : 'text.primary',
          }}
        >
          {entry.device}
        </Typography>

        {entry.error && (
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              display: 'block',
              mt: 0.2,
              color: ERROR_COLOR,
              opacity: 0.9,
            }}
          >
            {entry.error}
          </Typography>
        )}

        {isAlreadySynced && (
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              display: 'block',
              mt: 0.2,
              color: ALREADY_SYNCED_COLOR,
              opacity: 0.85,
            }}
          >
            Já sincronizado
          </Typography>
        )}
      </Box>

      <Typography
        sx={{
          fontSize: 11,
          color: 'text.disabled',
          flexShrink: 0,
          lineHeight: 1,
          pt: entry.error ? 0.3 : 0,
        }}
      >
        {typeLabel}
      </Typography>
    </Box>
  );
};

const FaceDeviceLog = ({ devices }: FaceDeviceLogProps) => {
  const done = devices.filter(
    (d) => d.status === 'ok' || d.status === 'already_synced',
  );
  const okOnly = devices.filter((d) => d.status === 'ok');
  const alreadySynced = devices.filter((d) => d.status === 'already_synced');
  const failed = devices.filter((d) => d.status === 'error');
  const active = devices.filter((d) => d.status === 'processing');
  const pending = devices.filter((d) => d.status === 'pending');
  const total = devices.length;
  const allDone = done.length === total && total > 0;
  const allFinished = done.length + failed.length === total && total > 0;

  const [showDone, setShowDone] = useState(false);
  const [showAlreadySynced, setShowAlreadySynced] = useState(false);
  const [showFailed, setShowFailed] = useState(true);

  // Monitora quais dispositivos acabaram de transicionar para 'ok' nesta renderização
  const prevStatusRef = useRef<Map<string, DeviceLogStatus>>(new Map());
  const prevFailedRef = useRef<DeviceLogEntry[]>([]);
  if (failed.length > 0) prevFailedRef.current = failed;
  const failedToRender = failed.length > 0 ? failed : prevFailedRef.current;
  const newlyDone = new Set<string>();
  devices.forEach((d) => {
    const prev = prevStatusRef.current.get(d.device);
    const isDoneNow = d.status === 'ok' || d.status === 'already_synced';
    const wasDoneBefore = prev === 'ok' || prev === 'already_synced';
    if (isDoneNow && prev !== undefined && !wasDoneBefore) {
      newlyDone.add(d.device);
    }
  });
  devices.forEach((d) => {
    prevStatusRef.current.set(d.device, d.status);
  });

  useEffect(() => {
    if (allDone) {
      setShowDone(true);
      setShowAlreadySynced(true);
    }
  }, [allDone]);

  const inProgressItems = [...active, ...pending];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.75,
        }}
      >
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
        >
          Dispositivos
        </Typography>
        <Typography
          variant="caption"
          color={
            failed.length > 0
              ? 'error.main'
              : allDone
                ? 'success.main'
                : 'text.secondary'
          }
        >
          {done.length + failed.length}/{total}
          {allFinished
            ? ` · ${Math.round((done.length / total) * 100)}% ok`
            : failed.length > 0
              ? ` · ${failed.length} falhou`
              : ''}
        </Typography>
      </Box>

      <Box
        sx={{
          border: (t) => `1px solid ${t.palette.divider}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          maxHeight: 300,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 2,
            backgroundColor: 'divider',
          },
        }}
      >
        {/* Grupo de falhas — topo, expandido por padrão; recolhe suavemente quando os erros são resolvidos */}
        <Collapse in={failed.length > 0} unmountOnExit>
          <Box>
            <Box
              onClick={() => setShowFailed((v) => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.6,
                cursor: 'pointer',
                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                backgroundColor: alpha(ERROR_COLOR, 0.07),
                '&:hover': { backgroundColor: alpha(ERROR_COLOR, 0.12) },
              }}
            >
              <ErrorIcon
                sx={{ fontSize: 17, color: ERROR_COLOR, flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontSize: 12.5,
                  color: ERROR_COLOR,
                  fontWeight: 600,
                }}
              >
                {failedToRender.length}{' '}
                {failedToRender.length === 1 ? 'falhou' : 'falharam'}
              </Typography>
              <IconButton size="small" sx={{ p: 0, color: ERROR_COLOR }}>
                {showFailed ? (
                  <ExpandLessIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>

            <Collapse in={showFailed}>
              {failedToRender.map((entry, i) => (
                <DeviceRow
                  key={`error-${entry.device}`}
                  entry={entry}
                  isLast={
                    i === failedToRender.length - 1 &&
                    inProgressItems.length === 0 &&
                    done.length === 0
                  }
                />
              ))}
            </Collapse>
          </Box>
        </Collapse>

        {/* Ativos + pendentes */}
        {inProgressItems.map((entry, i) => (
          <DeviceRow
            key={`active-${entry.device}`}
            entry={entry}
            isLast={i === inProgressItems.length - 1 && done.length === 0}
          />
        ))}

        {/* Grupo Já Sincronizados — entre os pendentes e os concluídos novos */}
        {alreadySynced.length > 0 && (
          <Box>
            <Box
              onClick={() => setShowAlreadySynced((v) => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.6,
                cursor: 'pointer',
                borderTop:
                  inProgressItems.length > 0 || failed.length > 0
                    ? (t) => `1px solid ${t.palette.divider}`
                    : 'none',
                borderBottom:
                  okOnly.length > 0
                    ? (t) => `1px solid ${t.palette.divider}`
                    : 'none',
                backgroundColor: alpha(ALREADY_SYNCED_COLOR, 0.07),
                '&:hover': {
                  backgroundColor: alpha(ALREADY_SYNCED_COLOR, 0.12),
                },
              }}
            >
              <DoneAllIcon
                sx={{
                  fontSize: 17,
                  color: ALREADY_SYNCED_COLOR,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontSize: 12.5,
                  color: ALREADY_SYNCED_COLOR,
                  fontWeight: 600,
                }}
              >
                {alreadySynced.length} já{' '}
                {alreadySynced.length === 1 ? 'sincronizado' : 'sincronizados'}
              </Typography>
              <IconButton
                size="small"
                sx={{ p: 0, color: ALREADY_SYNCED_COLOR }}
              >
                {showAlreadySynced ? (
                  <ExpandLessIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>

            <Collapse in={showAlreadySynced}>
              {alreadySynced.map((entry, i) => (
                <DeviceRow
                  key={`already-${entry.device}`}
                  entry={entry}
                  isLast={i === alreadySynced.length - 1 && okOnly.length === 0}
                  flash={newlyDone.has(entry.device)}
                />
              ))}
            </Collapse>
          </Box>
        )}

        {/* Grupo de concluídos — rodapé, recolhido por padrão */}
        {okOnly.length > 0 && (
          <Box>
            <Box
              onClick={() => setShowDone((v) => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.6,
                cursor: 'pointer',
                borderTop:
                  inProgressItems.length > 0 ||
                  failed.length > 0 ||
                  alreadySynced.length > 0
                    ? (t) => `1px solid ${t.palette.divider}`
                    : 'none',
                backgroundColor: (t) => alpha(t.palette.success.main, 0.06),
                '&:hover': {
                  backgroundColor: (t) => alpha(t.palette.success.main, 0.1),
                },
              }}
            >
              <CheckCircleIcon
                sx={{ fontSize: 17, color: 'success.main', flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  fontSize: 12.5,
                  color: 'success.main',
                  fontWeight: 600,
                }}
              >
                {okOnly.length}{' '}
                {okOnly.length === 1 ? 'concluído' : 'concluídos'}
              </Typography>
              <IconButton size="small" sx={{ p: 0, color: 'success.main' }}>
                {showDone ? (
                  <ExpandLessIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>

            <Collapse in={showDone}>
              {okOnly.map((entry, i) => (
                <DeviceRow
                  key={`ok-${entry.device}`}
                  entry={entry}
                  isLast={i === okOnly.length - 1}
                  flash={newlyDone.has(entry.device)}
                />
              ))}
            </Collapse>
          </Box>
        )}

        {total === 0 && (
          <Box sx={{ px: 1.5, py: 1.5, textAlign: 'center' }}>
            <Typography variant="caption" color="text.disabled">
              Nenhum dispositivo
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FaceDeviceLog;
